import { Link } from 'react-router-dom';
import { referenceGuides } from '../../data/referenceGuides';

/**
 * The index hero: the operational spine every guide sits on, with the
 * hand-offs where things actually break marked in.
 *
 * It doubles as the guide selector — each zone links to the guide that covers
 * that stretch of the chain. That is the argument the whole section makes:
 * no single system owns the chain, and the seams are where the money leaks.
 *
 * IMPLEMENTATION NOTE. The plan (section 5.1) specifies this as a commissioned
 * SVG asset with a thin wrapper, and Codex was right that rebuilding an
 * illustration out of divs produces brittle responsive geometry. This is the
 * middle path until artwork exists: semantic HTML for anything carrying text
 * (real text reflows, scales, and is selectable and readable by assistive tech)
 * and SVG only for the connector and break glyphs, which are pure decoration.
 * When a designer delivers real artwork, swap the body of this component —
 * the interface and the links stay put.
 */

interface Zone {
  slug: string;
  label: string;
  steps: string[];
  accentClass: string;
  dotClass: string;
}
const ZONES: Zone[] = [{
  slug: 'procurement-systems',
  label: 'Procurement',
  steps: ['Request', 'Source', 'Approve', 'Commit'],
  accentClass: 'text-guide-procurement',
  dotClass: 'bg-guide-procurement'
}, {
  slug: 'manufacturing-execution-systems',
  label: 'Manufacturing',
  steps: ['Plan', 'Build', 'Verify'],
  accentClass: 'text-guide-manufacturing',
  dotClass: 'bg-guide-manufacturing'
}, {
  slug: 'inventory-management-systems',
  label: 'Inventory',
  steps: ['Receive', 'Store', 'Pick', 'Ship'],
  accentClass: 'text-guide-inventory',
  dotClass: 'bg-guide-inventory'
}];
function BreakMarker() {
  return <span aria-hidden="true" className="relative flex items-center justify-center py-2" title="Hand-off" data-id="element-377" data-mp="2ln7r8s66u2ig">
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white/25 rotate-90" fill="none" data-id="element-378" data-mp="2uljqtjp18an1">
        <path d="M6 3v6l-3 3 3 3v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-379" data-mp="2ujp1zuan3ydo" />
        <path d="M18 3v6l3 3-3 3v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-380" data-mp="1x6oyksg1180u" />
      </svg>
    </span>;
}
export function SystemHandoffMap() {
  const titles = new Map(referenceGuides.map(g => [g.slug, g.title]));
  return <figure className="m-0" data-id="element-381" data-mp="261mu87ak6yvv">
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6" data-id="element-382" data-mp="2ayvdycbcvaz8">
        <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-5" data-id="element-383" data-mp="2fxptn5e10jf4">
          One chain, three systems
        </p>

        {/* The map lives in a half-width hero column, so the chain runs
            top-to-bottom and each zone's steps run across. Laying the three
            zones side by side clipped the third one out of the card. */}
        <ul className="flex flex-col gap-0 list-none" data-id="element-384" data-mp="qkcoxf204tdh">
          {ZONES.map((zone, i) => <li key={zone.slug} className="flex flex-col min-w-0" data-id="element-385" data-mp="ulenrus0b3ex" data-mpi={i}>
              <Link to={`/resources/reference-guides/${zone.slug}`} className="group rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 hover:border-white/20 hover:bg-white/[0.05] transition-colors" data-id="element-386" data-mp="n0klshgqxvun">
                <span className={`${zone.accentClass} text-xs font-bold uppercase tracking-widest`} data-id="element-387" data-mp="daj90ltufzv1">
                  {zone.label}
                </span>
                <span className="sr-only" data-id="element-388" data-mp="97cruj570o9s"> — read the {titles.get(zone.slug)} guide</span>
                <ol className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 list-none" data-id="element-389" data-mp="15yx711kxmw6l">
                  {zone.steps.map(step => <li key={step} className="flex items-center gap-2" data-id="element-390" data-mp="10ysnt3sn3nco">
                      <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${zone.dotClass} flex-shrink-0`} data-id="element-391" data-mp="wxgw8sq7gs1u" />
                      <span className="text-white/70 text-sm group-hover:text-white transition-colors" data-id="element-392" data-mp="s21ie0s76w5f">
                        {step}
                      </span>
                    </li>)}
                </ol>
              </Link>
              {i < ZONES.length - 1 && <BreakMarker data-id="element-393" data-mp="14h834e0ebuu2" />}
            </li>)}
        </ul>
      </div>

      <figcaption className="text-white/50 text-sm mt-3 leading-relaxed" data-id="element-394" data-mp="197wr3jcva9og">
        Every arrow is a hand-off. The hand-offs are where inventory goes wrong, spend goes
        unapproved, and work orders lose the plot.
      </figcaption>
    </figure>;
}